import { api } from './api';
import type {
  EvalSuite,
  EvalType,
  EvalGrader,
  SuiteCaseRef,
  SuiteTargetRef,
  PaginatedEvalSuiteResponse,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  code: number;
  message: string;
  data: T;
}

// Required: suiteCode, name, type. appId is create-only.
export interface EvalSuiteCreateRequest {
  suiteCode: string;
  name: string;
  type: EvalType;
  description?: string | null;
  version?: number | null;
  grader?: EvalGrader | null;
  cases?: SuiteCaseRef[];
  targetRef?: SuiteTargetRef | null;
  appId?: string;
}

export type EvalSuiteUpdateRequest = Partial<Omit<EvalSuiteCreateRequest, 'appId'>>;

export interface EvalSuiteListParams {
  page?: number;
  page_size?: number;
  search?: string;
  include_deleted?: boolean;
  type?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  filters?: string;
  app_id?: string;
  include_global?: boolean; // also include core/global rows (Show core toggle)
}

/** Re-scope preview shape (shared with ProjectRescopeDialog). */
export interface RescopePreview {
  canRescope: boolean;
  conflicts: Record<string, Record<string, number>>;
  references: Record<string, Record<string, number>>;
}

export const evalSuitesService = {
  list: async (params?: EvalSuiteListParams): Promise<PaginatedEvalSuiteResponse> => {
    const response = await api.get<PaginatedEvalSuiteResponse>('/api/v1/eval-suites/list', { params });
    return response.data;
  },

  getById: async (id: string): Promise<EvalSuite> => {
    const response = await api.get<ApiResponse<EvalSuite>>(`/api/v1/eval-suites/get/${id}`);
    return response.data.data;
  },

  create: async (data: EvalSuiteCreateRequest): Promise<EvalSuite> => {
    const response = await api.post<ApiResponse<EvalSuite>>('/api/v1/eval-suites/create', data);
    return response.data.data;
  },

  update: async (id: string, data: EvalSuiteUpdateRequest): Promise<EvalSuite> => {
    const response = await api.put<ApiResponse<EvalSuite>>(`/api/v1/eval-suites/update/${id}`, data);
    return response.data.data;
  },

  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/eval-suites/delete/${id}`);
    return response.data.data;
  },

  restore: async (id: string): Promise<{ message: string }> => {
    const response = await api.patch<ApiResponse<{ message: string }>>(`/api/v1/eval-suites/restore/${id}`);
    return response.data.data;
  },

  // Attach a case to a suite (idempotency guarded server-side).
  addCase: async (suiteId: string, caseRef: string, modelIdOverride?: string | null): Promise<EvalSuite> => {
    const response = await api.post<ApiResponse<EvalSuite>>(
      `/api/v1/eval-suites/${suiteId}/cases`,
      { caseRef, modelIdOverride: modelIdOverride ?? null }
    );
    return response.data.data;
  },

  // Detach a case from a suite.
  removeCase: async (suiteId: string, caseId: string): Promise<EvalSuite> => {
    const response = await api.delete<ApiResponse<EvalSuite>>(
      `/api/v1/eval-suites/${suiteId}/cases/${caseId}`
    );
    return response.data.data;
  },

  // Change a suite's owning project (null/empty = global). Platform admin only.
  rescopeProject: async (id: string, appId: string | null): Promise<EvalSuite> => {
    const response = await api.patch<{ data: EvalSuite }>(
      `/api/v1/eval-suites/${id}/project`,
      { appId: appId || null }
    );
    return response.data.data;
  },

  // Conflict preview before a re-scope. The BE enforces the (appId, suiteCode)
  // uniqueness on apply (409), so the preview is advisory and permissive here.
  getRescopePreview: async (): Promise<RescopePreview> => ({
    canRescope: true, conflicts: {}, references: {},
  }),
};
